import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { LinkButton, LoadingIndicator, Pagination } from 'components/common';
import { PageEmptyIndicator, PageSectionHeader } from 'components/page';
import { PathsAdminTile } from '.';

import type { ViewProps } from 'types';
import type { GetPathsRes } from 'shared-types/responses';

type Props = ViewProps & {
  onFilterDataReceived?: (
    data: GetPathsRes['pathsFilterOptions'] | null
  ) => void;
};

const PathsViewAdministered = ({
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 8,
  onFilterDataReceived,
  onNoData = null,
  queryParams = '',
  title
}: Props) => {
  const { messages, user } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, loading } = useApiData<GetPathsRes>({
    onDataUpdated: (data) => {
      // filter data is sent to parent
      if (onFilterDataReceived) {
        if (data?.pathsFilterOptions) {
          onFilterDataReceived(data?.pathsFilterOptions);
        } else {
          onFilterDataReceived(null);
        }
      }
    },
    url: `/admin/paths?pathType=administered&limit=${limitPerPage}&page=${page}${queryParams}`,
    onlyIf: Boolean(user)
  });

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  useEffect(() => {
    const hasData = !loading && Boolean(data?.paths?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  return (
    <div className="content">
      {loading ? (
        <LoadingIndicator />
      ) : data?.pathsCount > 0 ? (
        <div className="inner">
          <PageSectionHeader
            description={description}
            title={title || messages.coursesAdministered}
            titleCount={data?.pathsCount || 0}
          />

          <div className="paths">
            {data?.paths.map((path) => (
              <div className="path" key={path.id}>
                <PathsAdminTile path={path} />
              </div>
            ))}
          </div>

          {data?.pages > 1 && !isHomeView ? (
            <Pagination
              currentPage={page}
              numberOfPages={data?.pages || 1}
              onPageChange={({ page }) => setPage(page)}
            />
          ) : null}
        </div>
      ) : hideIfNoData ? null : (
        <>
          <PageSectionHeader
            description={description}
            title={title || messages.coursesAdministered}
            titleCount={0}
          />

          <PageEmptyIndicator
            icon="tasks"
            title={messages.pathsEmpty}
            description={messages.pathsEmptyAdministered}
          >
            <LinkButton
              category="light"
              href="/paths?type=all"
              size="small"
              text={messages.pathsSeeAllPaths}
            />
          </PageEmptyIndicator>
        </>
      )}

      <style jsx>{`
        .paths {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 30px;
        }
        .path {
          width: 100%;
        }
        @media (min-width: ${breakpoints.large}px) {
          .path {
            width: calc(50% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default PathsViewAdministered;
